import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X, ShieldStar, User, HandHeart } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/start-here", label: "Start Here" },
  { to: "/quiz", label: "Job Quiz" },
  { to: "/branches", label: "Branches" },
  { to: "/compare", label: "Compare" },
  { to: "/career-deepdive", label: "Career Deep Dive", premium: true },
  { to: "/faq", label: "FAQ" },
];

export default function Navbar({ onOpenAuth }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="h-9 w-9 rounded-md bg-[#0033A0] text-white flex items-center justify-center">
            <ShieldStar size={20} weight="fill" />
          </span>
          <span className="font-display font-black text-lg tracking-tight text-slate-900">
            Boot<span className="text-[#D90429]">To</span>Benefit
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  isActive ? "text-[#0033A0]" : "text-slate-700 hover:text-[#0033A0]"
                }`
              }
            >
              {n.label}
              {n.premium && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#D90429] text-white px-1.5 py-0.5 rounded">$1</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/donate"
            data-testid="nav-donate-btn"
            className="text-sm font-semibold text-[#D90429] hover:text-[#B00020] inline-flex items-center gap-1.5"
          >
            <HandHeart size={16} weight="fill" /> Donate
          </Link>
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm font-medium text-slate-700 hover:text-[#0033A0] flex items-center gap-2">
                <User size={18} weight="bold" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button onClick={logout} data-testid="logout-btn" className="text-sm font-medium text-slate-500 hover:text-slate-900">
                Log out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onOpenAuth?.("login")}
                data-testid="nav-login-btn"
                className="text-sm font-medium text-slate-700 hover:text-[#0033A0]"
              >
                Log in
              </button>
              <button
                onClick={() => onOpenAuth?.("register")}
                data-testid="nav-register-btn"
                className="inline-flex items-center justify-center bg-[#0033A0] hover:bg-[#002277] text-white text-sm font-semibold py-2.5 px-5 rounded-md transition-colors"
              >
                Get started
              </button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `text-base font-medium py-1 ${isActive ? "text-[#0033A0]" : "text-slate-800"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="border-t border-slate-200 pt-3 mt-1 flex flex-col gap-2">
              <Link to="/donate" onClick={() => setOpen(false)} data-testid="mobile-nav-donate" className="text-sm text-[#D90429] font-semibold inline-flex items-center gap-1.5">
                <HandHeart size={14} weight="fill" /> Donate
              </Link>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm text-slate-800 font-medium">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm text-slate-600">Log out</button>
                </>
              ) : (
                <>
                  <button onClick={() => { onOpenAuth?.("login"); setOpen(false); }} className="text-left text-sm text-slate-800 font-medium">Log in</button>
                  <button onClick={() => { onOpenAuth?.("register"); setOpen(false); }} className="text-left bg-[#0033A0] text-white py-2 px-4 rounded-md text-sm font-semibold">Get started</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
